import { useRef, useState, useEffect } from "react";
import {
  Group,
  Button,
  Stack,
  Text,
  Select,
  Box,
  Modal,
  SimpleGrid,
  Divider,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { ControllerRenderProps, FieldValues } from "react-hook-form";
import type { IUser } from "../UserList/UserList.types";
import dayjs from "dayjs";

import styles from "./CrewSignatureCapture.module.scss";

interface SignatureData {
  userId: string;
  userName: string;
  signatureImage: string;
  signatureDate: Date;
}

interface Props {
  field: ControllerRenderProps<FieldValues, string>;
  users?: IUser[];
  defaultUser?: string | null;
  isLoading?: boolean;
  title?: string;
  description?: string;
}

const CrewSignatureCapture = ({
  field,
  users = [],
  defaultUser = null,
  title = "Signature Capture",
  description = "Select crew member and sign below",
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    defaultUser,
  );
  const [opened, { open, close }] = useDisclosure(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Get current signature data from field value
  const currentSignature = field.value as SignatureData | null;
  const selectedUser = users.find(c => c.userid === selectedUserId);

  useEffect(() => {
    if (defaultUser) {
      setSelectedUserId(defaultUser);
    }
  }, [defaultUser]);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
    setHasDrawn(false);
  }, [currentSignature?.signatureImage]);

  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    setIsDrawing(true);

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const pos = getMousePos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const pos = getMousePos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    !hasDrawn && setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setHasDrawn(false);
    }
  };

  const saveSignature = () => {
    if (!selectedUser || !canvasRef.current) return;
    const signatureImage = canvasRef.current.toDataURL("image/png");
    const signatureDate = new Date();

    const signatureData: SignatureData = {
      userId: selectedUser.userid,
      userName: selectedUser.full_name,
      signatureImage,
      signatureDate,
    };

    field.onChange(signatureData);
    close();
  };

  const userSelect = users.map(crew => ({
    value: crew.userid,
    label: `${crew.ref} - ${crew.rolename} ${crew.full_name}`,
  }));

  return (
    <>
      <Group gap="xs" noWrap>
        <Button
          onClick={open}
          variant="outline"
          size="xs"
          style={{
            width: "100%",
          }}>
          {currentSignature ? `Signed by ${currentSignature.userName}` : "Sign"}
        </Button>
      </Group>

      <Modal opened={opened} onClose={close} title={title} size="lg" centered>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {description}
          </Text>

          <Select
            label="Select Crew Member"
            placeholder="Choose a crew member"
            data={userSelect}
            value={selectedUserId}
            onChange={setSelectedUserId}
            searchable
            size="sm"
            withinPortal
          />

          {selectedUserId && (
            <>
              <Divider />

              <div>
                <Text size="sm" fw={500} mb="xs">
                  Signature Area
                </Text>
                <Box className={styles.canvasWrapper}>
                  <canvas
                    ref={canvasRef}
                    style={{
                      display: "block",
                      cursor: "crosshair",
                      width: "100%",
                      height: "200px",
                    }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                  />
                </Box>
              </div>

              <SimpleGrid cols={2} spacing="sm">
                <Button onClick={clearSignature} variant="outline" size="sm">
                  Clear
                </Button>
                <Button
                  onClick={saveSignature}
                  color="blue"
                  size="sm"
                  disabled={!hasDrawn}>
                  Save Signature
                </Button>
              </SimpleGrid>
            </>
          )}

          {currentSignature && (
            <>
              <Divider />

              <Box>
                <Group justify="left" gap="xs">
                  <Text size="sm">Signed by {currentSignature.userName}</Text>
                  <Text size="sm">
                    {dayjs(currentSignature.signatureDate).format(
                      "DD MMM YYYY HH:mm",
                    )}
                  </Text>
                </Group>

                <Box mt="md">
                  <Text size="xs" c="dimmed" mb="xs">
                    Signature
                  </Text>
                  <Box className={styles.signaturePreview}>
                    <img
                      src={currentSignature.signatureImage}
                      alt="Signature"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100px",
                        display: "block",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </Modal>
    </>
  );
};

export default CrewSignatureCapture;
